import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  List,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";
import {
  listConversations,
  listMessages,
  sendMessage,
  startPrivateConversation,
} from "services/chatService";

const { Paragraph, Text } = Typography;

const userIdOf = (user) => user?.id || user?.userId || "";

const pageDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const messageText = (content) => {
  if (content === null || content === undefined) return "";
  if (typeof content === "string") return content;
  if (typeof content === "number" || typeof content === "boolean") return String(content);
  return (
    content.text ||
    content.message ||
    content.body ||
    content.value ||
    JSON.stringify(content)
  );
};

const conversationTitle = (conversation, actorId) => {
  if (conversation?.name) return conversation.name;
  const participants = conversation?.participants || [];
  const otherUsers = participants
    .map((participant) => participant?.user)
    .filter((participantUser) => participantUser?.id !== actorId);

  const names = otherUsers
    .map(
      (participantUser) =>
        [participantUser?.firstName, participantUser?.lastName].filter(Boolean).join(" ").trim() ||
        participantUser?.username ||
        participantUser?.email ||
        participantUser?.id,
    )
    .filter(Boolean);

  return names.join(", ") || `${conversation?.type || "Conversation"} ${String(conversation?.id || "").slice(0, 8)}`;
};

const ChatSpace = () => {
  const location = useLocation();
  const { user } = useAuth();
  const actorId = userIdOf(user);
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialConversationId = initialParams.get("conversationId") || "";
  const initialParticipantId = initialParams.get("participantId") || "";
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(initialConversationId);
  const [messages, setMessages] = useState([]);
  const [participantId, setParticipantId] = useState(initialParticipantId);
  const [draft, setDraft] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (initialConversationId) setSelectedConversationId(initialConversationId);
    if (initialParticipantId) setParticipantId(initialParticipantId);
  }, [initialConversationId, initialParticipantId]);

  const visibleConversations = useMemo(() => {
    if (!actorId) return conversations;
    return conversations.filter((conversation) => {
      const participants = conversation?.participants || [];
      return (
        participants.length === 0 ||
        participants.some((participant) => participant?.user?.id === actorId)
      );
    });
  }, [actorId, conversations]);

  const selectedConversation = useMemo(
    () =>
      visibleConversations.find(
        (conversation) => conversation?.id === selectedConversationId,
      ) || null,
    [selectedConversationId, visibleConversations],
  );

  const loadConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      setLoadError("");
      const result = await listConversations();
      setConversations(Array.isArray(result) ? result : []);
      if (!selectedConversationId && result?.[0]?.id) {
        setSelectedConversationId(result[0].id);
      }
    } catch (error) {
      setLoadError(error?.message || "Failed to load conversations.");
    } finally {
      setLoadingConversations(false);
    }
  }, [selectedConversationId]);

  const loadSelectedMessages = useCallback(async () => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }

    try {
      setLoadingMessages(true);
      const result = await listMessages(selectedConversationId);
      setMessages(Array.isArray(result) ? result : []);
    } catch (error) {
      message.error(error?.message || "Failed to load messages.");
    } finally {
      setLoadingMessages(false);
    }
  }, [selectedConversationId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadSelectedMessages();
  }, [loadSelectedMessages]);

  const handleStartPrivateConversation = async () => {
    const nextParticipantId = participantId.trim();
    if (!actorId || !nextParticipantId) return;

    try {
      setStarting(true);
      const conversation = await startPrivateConversation({
        actorId,
        participants: [nextParticipantId],
      });
      setParticipantId("");
      await loadConversations();
      if (conversation?.id) setSelectedConversationId(conversation.id);
      message.success("Private chat started.");
    } catch (error) {
      message.error(error?.message || "Failed to start chat.");
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!actorId || !selectedConversationId || !text) return;

    try {
      setSending(true);
      await sendMessage({
        actorId,
        conversationId: selectedConversationId,
        content: text,
      });
      setDraft("");
      await loadSelectedMessages();
    } catch (error) {
      message.error(error?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<MessageOutlined />}
          title="Chat Space"
          subtitle="Workspace-friendly conversations for Olreadia collaboration"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={loadConversations}
          style={{
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            color: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreenSoft,
            fontWeight: 600,
          }}
        >
          Refresh
        </Button>
      </div>

      {loadError ? (
        <Alert
          type="warning"
          showIcon
          message="Chat Space could not fully load"
          description={loadError}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 360px) minmax(0, 1fr)",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Start private chat</Text>}
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.oliveebBlueSoft }}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Input
                value={participantId}
                onChange={(event) => setParticipantId(event.target.value)}
                placeholder="Participant user ID"
                prefix={<UserAddOutlined />}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                loading={starting}
                disabled={!actorId || !participantId.trim()}
                onClick={handleStartPrivateConversation}
              />
            </Space.Compact>
          </Card>

          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Conversations</Text>}
            extra={<Tag color="blue">{visibleConversations.length}</Tag>}
            style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}
          >
            {loadingConversations ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : visibleConversations.length ? (
              <List
                dataSource={visibleConversations}
                renderItem={(conversation) => (
                  <List.Item
                    onClick={() => setSelectedConversationId(conversation.id)}
                    style={{
                      cursor: "pointer",
                      borderRadius: 8,
                      paddingInline: 10,
                      background:
                        conversation.id === selectedConversationId
                          ? OLIVEEB_COLORS.oliveebBlueSoft
                          : "transparent",
                    }}
                  >
                    <List.Item.Meta
                      avatar={<MessageOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} />}
                      title={<Text strong>{conversationTitle(conversation, actorId)}</Text>}
                      description={
                        <Space wrap size={[4, 4]}>
                          <Tag>{conversation.type || "CHAT"}</Tag>
                          <Text type="secondary">
                            {conversation.participants?.length || 0} participants
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations found" />
            )}
          </Card>
        </Space>

        <Card
          title={
            <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
              {selectedConversation
                ? conversationTitle(selectedConversation, actorId)
                : "Messages"}
            </Text>
          }
          extra={selectedConversation ? <Tag color="green">{selectedConversation.type}</Tag> : null}
          style={{
            borderRadius: 8,
            borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
            minHeight: 560,
          }}
          bodyStyle={{
            minHeight: 500,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
            {loadingMessages ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : selectedConversationId && messages.length ? (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                {messages.map((item) => {
                  const mine = item.senderId === actorId;
                  return (
                    <div
                      key={item.id || `${item.senderId}-${item.timestamp}`}
                      style={{
                        display: "flex",
                        justifyContent: mine ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "76%",
                          borderRadius: 8,
                          padding: "10px 12px",
                          background: mine
                            ? OLIVEEB_COLORS.oliveebBlueSoft
                            : OLIVEEB_COLORS.bgSoft,
                          border: `1px solid ${
                            mine ? OLIVEEB_COLORS.oliveebBlueSoft : OLIVEEB_COLORS.border
                          }`,
                        }}
                      >
                        <Paragraph style={{ marginBottom: 6 }}>
                          {messageText(item.content)}
                        </Paragraph>
                        <Space wrap size={[6, 2]}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {mine ? "You" : String(item.senderId || "").slice(0, 8)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {pageDate(item.timestamp)}
                          </Text>
                          {item.status ? <Tag color="default">{item.status}</Tag> : null}
                        </Space>
                      </div>
                    </div>
                  );
                })}
              </Space>
            ) : selectedConversationId ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages yet" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a conversation" />
            )}
          </div>

          <Space.Compact style={{ width: "100%" }}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
              disabled={!selectedConversationId}
              placeholder={
                selectedConversationId
                  ? "Write a message"
                  : "Select or start a conversation first"
              }
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!selectedConversationId || !draft.trim()}
              onClick={handleSend}
            >
              Send
            </Button>
          </Space.Compact>
        </Card>
      </div>
    </div>
  );
};

export default ChatSpace;
